import type { KeyboardEvent } from "react";
import { styled } from "tamagui";
import { ChevronDown as ChevronDownIcon } from "@tamagui/lucide-icons";
import { pillSizes, pillSlotPadding } from "../themes/token-pill";
import { shadow } from "../themes/token-shadow";
import { Avatar, type AvatarProps } from "./Avatar";
import { GlassScope } from "./Glass/GlassScope";
import { GlassSurface } from "./Glass/GlassSurface";
import { useInHeader } from "./HeaderButton";
import { Icon } from "./Icon";
import { focusVisibleRingStyle } from "./interactive/styles";
import { useInteractiveElement } from "./interactive/useInteractiveElement";
import { motionTransition } from "./Motion/motion";
import { pillTextTruncation, StyledPill, type StyledPillProps } from "./StyledPill";
import { Subtitle2 } from "./Typography";
const IconPillLabel = styled(Subtitle2, pillTextTruncation);

// One size, 40 (`$si5`), with the avatar inset by the pill's vertical padding.
const PILL_HEIGHT = "$si5";
const PILL = pillSizes[PILL_HEIGHT];
const PRESS = "default";
type IconPillVariant = {
  variant?: "static";
  onKeyDown?: undefined;
} | {
  /**
   * Triggers a menu: adds the chevron, a tab stop, Enter/Space, press and
   * focus states, and hover on the light tone. The menu owns the press.
   */
  variant: "dropdown";
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  role?: StyledPillProps["role"];
  "aria-label"?: string;
  "aria-expanded"?: boolean;
  "aria-haspopup"?: "listbox";
  "aria-controls"?: string;
  "aria-activedescendant"?: string;
};
export type IconPillProps = IconPillVariant & {
  /** Who or what the pill names; the pill sets its size and shape. */
  avatar: AvatarProps;
  label: string;
  testID?: string;
  /** StyledPill has no dark status, so `dark` takes the inverse overlay. In a header, the header's tone wins. */
  tone?: "light" | "dark";
};

/** Names who or what a page is about; as a dropdown, picks between them. Glass in a header, unless iOS 26 draws it. A status is a Badge; a removable selection is a Chip. */
export function IconPill({
  avatar,
  label,
  testID,
  variant = "static",
  tone = "light",
  onKeyDown,
  ...aria
}: IconPillProps) {
  const dropdown = variant === "dropdown";
  const header = useInHeader();
  const dark = header ? header.tone === "inverse" : tone === "dark";
  const {
    interactiveProps,
    renderTag
  } = useInteractiveElement({
    kind: dropdown ? "button" : "static"
  });
  // The menu wrapping the pill owns the press; the hook's Enter/Space clicks
  // through to it.
  const {
    onKeyDown: ownKeyDown,
    onPress: _ownPress,
    ...focusProps
  } = interactiveProps;
  const pill = <StyledPill testID={testID} {...header ? {
    backgroundColor: "$transparent"
  } : dark ? {
    backgroundColor: "$utilityOverlayInverse"
  } : {
    ...shadow.subtle,
    backgroundColor: "$surface2"
  }} borderWidth={0} overflow="visible"
  // Grows with large type rather than clipping the label.
  minHeight={PILL_HEIGHT} gap={PILL.gap} {...pillSlotPadding(PILL_HEIGHT, {
    trailing: dropdown
  })} paddingLeft="$sp0.5" {...dropdown ? {
    ...focusProps,
    ...aria,
    ...motionTransition(PRESS),
    render: renderTag,
    press: PRESS,
    // The interactive variant's hover fill is a light surface: only the light tone off glass takes it.
    ...(dark || header ? {
      cursor: "pointer",
      focusVisibleStyle: focusVisibleRingStyle
    } : {
      interactive: true
    }),
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      onKeyDown?.(event);
      if (!event.defaultPrevented) {
        ownKeyDown?.(event);
      }
    }
  } : {}}>
      <Avatar {...avatar} size="small" shape="circle" aria-hidden />
      <IconPillLabel flexShrink={1} numberOfLines={1} {...dark ? {
      color: "$textInverse"
    } : {}}>
        {label}
      </IconPillLabel>
      {dropdown ? <Icon icon={ChevronDownIcon} size="$iconXSmall" color={dark ? "$iconInverseSubtle" : "$iconDefault"} /> : null}
    </StyledPill>;
  if (!header || header.systemGlass) {
    return pill;
  }
  const glass = <GlassSurface appearance={dark ? "dark" : "auto"} interactive={dropdown} shadow="control" borderRadius="$brFull">
      {pill}
    </GlassSurface>;
  return dark ? <GlassScope prominence="prominent" data-sentry-element="GlassScope" data-sentry-component="IconPill" data-sentry-source-file="IconPill.tsx">{glass}</GlassScope> : glass;
}